import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import type {
  ClassRosterItemView,
  LcTestKindKey,
  LearnerHeaderView,
  LearnerSummaryStatsView,
} from '@/types/coaching'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import { useTests } from '@/hooks/coaching/useTests'
import { AttendanceStatusChip } from '@/components/learner/profile/AttendanceStatusChip'
import { learnerHandle } from '@/utils/learnerEmail'

interface Props {
  learner: LearnerHeaderView
  summary: LearnerSummaryStatsView
  /** 로스터 카드의 원생 상세(아이디·학년·성별·학부모폰·상태). 미선택/미로드 시 일부 필드는 '-'. */
  rosterItem?: ClassRosterItemView
  /** 원생 관리 버튼 슬롯(등하원/결석/다음수업/레슨가이드). */
  actions?: ReactNode
  /** 오늘 수업 등 요약 화면에서 원생 상세로 이동할 경로. */
  detailHref?: string
  /** 원생 상세에서 평균 테스트 점수 클릭 시 완료 테스트 목록을 연다. */
  onTestAverageClick?: (kind: LcTestKindKey) => void
}

const DASH = '-'

/**
 * 원생 정보 & 학습 상태 패널 — 상단 이름+상태+원생관리 버튼, 하단 3섹션(기본/수업/학습정보).
 * 기본정보는 로스터(rosterItem), 학습정보는 라이브러리/테스트 집계를 소비한다.
 */
export function LearnerHistoryHeader({ learner, summary, rosterItem, actions, detailHref, onTestAverageClick }: Props) {
  const { t } = useTranslation(['coaching', 'admin'])
  const library = useLibrary(learner.learnerId)
  const tests = useTests(learner.learnerId)

  const gradeLabel = (g: string | null | undefined) =>
    g ? t(`admin:dashboard.gradeLevel.${g}`, { defaultValue: g }) : DASH
  const genderLabel = (g: string | null | undefined) =>
    g === 'male'
      ? t('admin:students.form.genderMale')
      : g === 'female'
        ? t('admin:students.form.genderFemale')
        : DASH

  // 학습정보 집계 — 과정(커리큘럼) 단위 누적/완료.
  const tree = library.data?.tree ?? []
  const courseTitle = tree.map((c) => c.title).join(', ') || learner.productTitle || DASH
  const totalLessons = tree.reduce((s, c) => s + c.lessonCount, 0)
  const doneLessons = tree.reduce((s, c) => s + c.completedLessonCount, 0)
  const completionPct = totalLessons > 0 ? Math.round((doneLessons / totalLessons) * 100) : 0
  const completionValue =
    totalLessons > 0
      ? t('infoPanel.completionValue', { pct: completionPct, done: doneLessons, total: totalLessons })
      : `${completionPct}%`
  const pct = (v: number | null | undefined) => (v != null ? `${v}%` : DASH)
  const remainingLessons = learner.lessonQuota
    ? (learner.lessonQuota.remainingLessons ??
      Math.max(0, learner.lessonQuota.grantedLessons - learner.lessonQuota.usedLessons))
    : null

  const status = rosterItem?.attendanceStatus
  const statusChip = (
    <AttendanceStatusChip status={status} />
  )

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      {/* 상단: 이름 + 상태 + 원생 관리 버튼 */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{learner.fullName}</h1>
          {statusChip}
          {detailHref ? (
            <Link
              to={detailHref}
              className="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-lc-mint-300 hover:bg-lc-mint-100 hover:text-lc-mint-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-mint-500"
            >
              {t('infoPanel.viewDetail')}
              <ChevronRight className="size-3.5" aria-hidden="true" />
            </Link>
          ) : null}
        </div>
        {actions}
      </div>

      {/* 하단: 기본정보 / 수업정보 / 학습정보 */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <InfoSection title={t('infoPanel.sectionBasic')}>
          <Field label={t('roster.colId')} value={rosterItem ? learnerHandle(rosterItem.email) : DASH} />
          <Field label={t('roster.colGrade')} value={gradeLabel(rosterItem?.gradeLevel)} />
          <Field label={t('roster.colGender')} value={genderLabel(rosterItem?.gender)} />
          <Field label={t('roster.colGuardianPhone')} value={rosterItem?.guardianPhone ?? DASH} />
        </InfoSection>

        <InfoSection title={t('infoPanel.sectionClass')}>
          <Field label={t('infoPanel.className')} value={learner.className ?? DASH} />
          <Field
            label={t('infoPanel.sessionRound')}
            value={
              learner.sessionRound != null
                ? t('infoPanel.sessionRoundValue', { n: learner.sessionRound })
                : DASH
            }
          />
          {learner.lessonQuota && (
            <>
              <Field label={t('infoPanel.grantedLessons')} value={String(learner.lessonQuota.grantedLessons)} />
              <Field label={t('infoPanel.usedLessons')} value={String(learner.lessonQuota.usedLessons)} />
              <Field label={t('infoPanel.remainingLessons')} value={String(remainingLessons)} />
            </>
          )}
        </InfoSection>

        <InfoSection title={t('infoPanel.sectionLearning')}>
          <Field label={t('infoPanel.course')}>
            <CourseChip title={courseTitle} />
          </Field>
          <Field label={t('infoPanel.completion')} value={completionValue} />
          <Field label={t('infoPanel.avgLessonRate')} value={`${summary.avgCorrectRate}%`} />
          <Field label={t('infoPanel.avgChapterTest')} value={pct(tests.data?.avgChapterScore)}>
            <AverageValue
              value={tests.data?.avgChapterScore}
              onClick={onTestAverageClick ? () => onTestAverageClick('CHAPTER') : undefined}
            />
          </Field>
        </InfoSection>
      </div>
    </section>
  )
}

function InfoSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  )
}

function Field({ label, value, children }: { label: string; value?: string; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-gray-900">{children ?? value}</dd>
    </div>
  )
}

function CourseChip({ title }: { title: string }) {
  return (
    <span
      data-testid="learning-course-chip"
      className="inline-flex max-w-full items-center truncate rounded-full border border-lc-mint-200 bg-lc-mint-100 px-2.5 py-0.5 text-xs font-semibold text-lc-mint-900"
    >
      {title}
    </span>
  )
}

function AverageValue({ value, onClick }: { value: number | null | undefined; onClick?: () => void }) {
  const display = value != null ? `${value}%` : DASH
  if (!onClick || value == null) return display

  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded text-right font-medium text-gray-900 underline-offset-2 hover:text-lc-mint-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-lc-mint-500"
    >
      {display}
    </button>
  )
}
